import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  defaultAnimateLayoutChanges,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from '../stylesheets/Todo.module.css';
import TodoItem from './TodoItem.jsx';
import storage from './storage.js';
import { getAppSurface, track, trackOncePerPage } from './analytics.js';

const STARTER_TODOS = [
  {
    text: 'Hi there! Welcome to FocusTodo',
    done: false,
    id: 0,
  },
  {
    text: 'Track your tasks here. On the left, you can write notes or manage your time with the pomodoro technique (25m working sessions separated by breaks).',
    done: false,
    id: 1,
  },
  {
    text: 'Tip: Use shift + enter for multiline to-dos!',
    done: false,
    id: 2,
  },
];

function getTimeBasedGreeting() {
  const hours = new Date().getHours();
  if (hours > 3 && hours < 12) return 'Good morning, ';
  if (hours >= 12 && hours < 18) return 'Good afternoon, ';
  return 'Good evening, ';
}

function getEasterEggData() {
  const today = new Date();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = days[today.getDay()];

  let message = getTimeBasedGreeting();
  let name = 'Chloe 💙';

  if (Math.floor((Math.random() * 3) + 1) === 3) {
    if (day === 'Monday') {
      message = 'Himnae through Monday ';
    } else if (day === 'Wednesday') {
      message = 'Hwaiting through hump day ';
    } else if (day === 'Thursday') {
      message = 'Almost the weekend! Himnae ';
    }
  }

  if (Math.floor((Math.random() * 4) + 1) === 4) {
    const messages = ['화이팅 ', '힘내 ', '사랑해 ', '아름다운 ', '안녕하세요 ', "어지니's favourite person ", '사랑스러운 '];
    message = messages[Math.floor(Math.random() * messages.length)];
  }

  if (Math.floor((Math.random() * 4) + 1) === 4) {
    const names = ['지유나 😍', '귀요미 🙈', '예쁜이 💖', '지유나 🙈', ' Chloe 😎', 'Jiyuna 😎', '지윤씨 🙈', '아르미 💖', '봉봉 🐝', '여봉 💕', '엽시 ✨'];
    name = names[Math.floor(Math.random() * names.length)];
  }

  return {
    message,
    name,
  };
}

function sanitizeText(text) {
  return text.replace(/[^a-z0-9]/gi, '').length > 0;
}

function resizeTextarea(textarea) {
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}

function isApplePlatform() {
  if (typeof navigator === 'undefined') return true;

  const platform =
    (navigator.userAgentData && navigator.userAgentData.platform) ||
    navigator.platform ||
    navigator.userAgent ||
    '';
  const normalizedPlatform = platform.toLowerCase();

  return (
    normalizedPlatform.includes('mac') ||
    normalizedPlatform.includes('iphone') ||
    normalizedPlatform.includes('ipad') ||
    normalizedPlatform.includes('ipod')
  );
}

function nowIsoTimestamp() {
  return new Date().toISOString();
}

function readTodoTimestamp(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length === 0 || Number.isNaN(Date.parse(trimmed))) return '';
  return new Date(trimmed).toISOString();
}

function hydrateTodo(todo, { isCompletedList = false } = {}) {
  const normalizedCreatedAt = readTodoTimestamp(todo.createdAt);
  const normalizedCompletedAt = readTodoTimestamp(todo.completedAt);
  const createdAt = normalizedCreatedAt;
  const completedAt = isCompletedList ? normalizedCompletedAt : '';

  return {
    ...todo,
    createdAt,
    completedAt,
  };
}

function createStarterTodos() {
  return STARTER_TODOS.map((todo) => ({
    ...todo,
    createdAt: '',
    completedAt: '',
  }));
}

function SortableTodoItem({ todo, onDelete, onToggle, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: todo.id,
    transition: {
      duration: 220,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
    },
    animateLayoutChanges: (args) => defaultAnimateLayoutChanges(args),
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
    willChange: 'transform',
    width: '100%',
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TodoItem
        todo={todo}
        text={todo.text}
        id={todo.id}
        isCompleted={false}
        deleteTodo={onDelete}
        toggleCompleted={onToggle}
        editEntry={onEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  );
}

SortableTodoItem.propTypes = {
  todo: PropTypes.shape({
    id: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    completedAt: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default function Todo({ message = 'Good evening, ', onHighestIdChange = null }) {
  const [name, setName] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('name') || '""') || '';
    } catch (e) {
      return '';
    }
  });
  const [highestId, setHighestId] = useState(0);
  const [easterEggMessage, setEasterEggMessage] = useState('');
  const [easterEggName, setEasterEggName] = useState('Chloe');
  const [todos, setTodos] = useState([]);
  const [completedTodos, setCompletedTodos] = useState([]);
  const [newTodoValue, setNewTodoValue] = useState('');
  const [activeDragId, setActiveDragId] = useState(null);
  const [activeDragWidth, setActiveDragWidth] = useState(null);
  const [nameHearts, setNameHearts] = useState([]);

  const nameInputRef = useRef(null);
  const newTodoRef = useRef(null);
  const heartSequenceRef = useRef(0);
  const heartTimeoutsRef = useRef([]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const trackTodosFeatureUse = useCallback(() => {
    trackOncePerPage('feature_use_todos', {
      app_surface: getAppSurface(),
      feature_name: 'todos',
    });
  }, []);
  const usesMetaShortcut = useMemo(() => isApplePlatform(), []);
  const shortcutHintText = usesMetaShortcut ? '⌘ K' : 'Ctrl K';
  const shortcutLabel = usesMetaShortcut ? 'Command+K' : 'Ctrl+K';

  const focusNewTodoInput = useCallback(() => {
    if (!newTodoRef.current) return;

    newTodoRef.current.focus();

    const cursorPosition = newTodoRef.current.value.length;
    if (typeof newTodoRef.current.setSelectionRange === 'function') {
      newTodoRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, []);

  useEffect(() => {
    const easterEgg = getEasterEggData();
    setEasterEggMessage(easterEgg.message);
    setEasterEggName(easterEgg.name);

    let isMounted = true;

    storage
      .loadState()
      .then((result) => {
        if (!isMounted) return;

        const hasPersistedTodoData =
          (result.state.todos && result.state.todos.length > 0) ||
          (result.state.completedTodos && result.state.completedTodos.length > 0) ||
          result.state.highestId > 0;

        if (hasPersistedTodoData) {
          setName(result.state.name || '');
          setHighestId(result.state.highestId || 0);
          setTodos((result.state.todos || []).map((todo) => hydrateTodo(todo, { isCompletedList: false })));
          setCompletedTodos((result.state.completedTodos || []).map((todo) => hydrateTodo(todo, { isCompletedList: true })));
          return;
        }

        setHighestId(2);
        setTodos(createStarterTodos());
        setCompletedTodos([]);
      })
      .catch(() => {
        setHighestId(2);
        setTodos(createStarterTodos());
        setCompletedTodos([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    storage.saveTodoState({
      name,
      highestId,
      todos,
      completedTodos,
    });
  }, [name, highestId, todos, completedTodos]);

  useEffect(() => {
    if (onHighestIdChange) onHighestIdChange(highestId);
  }, [highestId, onHighestIdChange]);

  useEffect(() => {
    resizeTextarea(newTodoRef.current);
  }, [newTodoValue]);

  useEffect(() => {
    const handleWindowKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key.toLowerCase() !== 'k') return;
      if (event.altKey || event.shiftKey) return;

      const matchedShortcut = usesMetaShortcut
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;

      if (!matchedShortcut) return;

      event.preventDefault();
      focusNewTodoInput();
    };

    window.addEventListener('keydown', handleWindowKeyDown);

    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown);
    };
  }, [focusNewTodoInput, usesMetaShortcut]);

  const handleNameEnter = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const nextName = event.currentTarget.value.trim();
      if (!nextName || !sanitizeText(nextName)) return;
      setName(nextName);
    }
  };

  const addNewTodo = useCallback(() => {
    if (!newTodoValue || !sanitizeText(newTodoValue)) return false;

    const incrementId = highestId + 1;
    const newTodo = {
      id: incrementId,
      text: newTodoValue,
      done: false,
      createdAt: nowIsoTimestamp(),
      completedAt: '',
    };

    setHighestId(incrementId);
    setTodos((current) => [newTodo, ...current]);
    setNewTodoValue('');
    trackTodosFeatureUse();
    track('todo_action', {
      app_surface: getAppSurface(),
      action_type: 'add',
    });
    return true;
  }, [highestId, newTodoValue, trackTodosFeatureUse]);

  const handleTodoEnter = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.currentTarget.blur();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      addNewTodo();
    }
  };

  const handleAddTodoClick = () => {
    addNewTodo();
    focusNewTodoInput();
  };

  const clearNameHeartTimeouts = useCallback(() => {
    heartTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    heartTimeoutsRef.current = [];
  }, []);

  const spawnNameHeart = useCallback(() => {
    const heartId = heartSequenceRef.current + 1;
    heartSequenceRef.current = heartId;

    const duration = 2200 + Math.floor(Math.random() * 900);
    const left = -10 + (Math.random() * 120);
    const originY = -12 + (Math.random() * 132);
    const radialX = left - 50;
    const radialY = originY - 50;
    const radialDistance = Math.hypot(radialX, radialY);
    const burstAngle = radialDistance < 6 ? Math.random() * Math.PI * 2 : Math.atan2(radialY, radialX);
    const burstDistance = 8 + (Math.random() * 8);
    const burstX = Math.round(Math.cos(burstAngle) * burstDistance);
    const burstY = Math.round(Math.sin(burstAngle) * burstDistance);
    const upwardLift = 22 + Math.floor(Math.random() * 20);
    const riseMid = Math.round(burstY - (upwardLift * (0.68 + (Math.random() * 0.16))));
    const riseEnd = Math.round(burstY - (upwardLift * (1.08 + (Math.random() * 0.22))));
    const driftMid = Math.round((burstX * (0.64 + (Math.random() * 0.18))) + (-5 + (Math.random() * 10)));
    const driftEnd = Math.round((burstX * (1.02 + (Math.random() * 0.24))) + (-10 + (Math.random() * 20)));

    const heartData = {
      id: heartId,
      left,
      originY,
      size: 10 + Math.floor(Math.random() * 6),
      duration,
      offsetX: -10 + Math.floor(Math.random() * 21),
      burstX,
      burstY,
      driftMid,
      riseMid,
      driftEnd,
      riseEnd,
      rotateStart: -4 + Math.floor(Math.random() * 9),
      rotateEnd: -9 + Math.floor(Math.random() * 19),
      scaleStart: (0.68 + Math.random() * 0.24).toFixed(2),
      scaleMid: (0.86 + Math.random() * 0.18).toFixed(2),
      scaleEnd: (0.92 + Math.random() * 0.32).toFixed(2),
      peakOpacity: (0.72 + Math.random() * 0.2).toFixed(2),
    };

    setNameHearts((current) => {
      const next = [...current, heartData];
      return next.length > 8 ? next.slice(next.length - 8) : next;
    });

    const timeoutId = window.setTimeout(() => {
      setNameHearts((current) => current.filter((heart) => heart.id !== heartId));
      heartTimeoutsRef.current = heartTimeoutsRef.current.filter((activeId) => activeId !== timeoutId);
    }, duration + 140);

    heartTimeoutsRef.current.push(timeoutId);
  }, []);

  const deleteTodo = useCallback(
    (todoId) => {
      trackTodosFeatureUse();
      const inActiveList = todos.some((todo) => todo.id === todoId);
      track('todo_action', {
        app_surface: getAppSurface(),
        action_type: 'delete',
        list_type: inActiveList ? 'active' : 'completed',
      });
      if (inActiveList) {
        setTodos((current) => current.filter((todo) => todo.id !== todoId));
        return;
      }

      setCompletedTodos((current) => current.filter((todo) => todo.id !== todoId));
    },
    [todos, trackTodosFeatureUse],
  );

  const toggleCompleted = useCallback(
    (todoToToggle) => {
      trackTodosFeatureUse();
      const currentlyActive = todos.some((todo) => todo.id === todoToToggle.id);
      track('todo_action', {
        app_surface: getAppSurface(),
        action_type: currentlyActive ? 'complete' : 'restore',
      });

      if (currentlyActive) {
        const completedAt = nowIsoTimestamp();
        setTodos((current) => current.filter((todo) => todo.id !== todoToToggle.id));
        setCompletedTodos((current) => [
          {
            ...todoToToggle,
            createdAt: readTodoTimestamp(todoToToggle.createdAt),
            completedAt,
          },
          ...current,
        ]);
        return;
      }

      setCompletedTodos((current) => current.filter((todo) => todo.id !== todoToToggle.id));
      setTodos((current) => [
        {
          ...todoToToggle,
          createdAt: readTodoTimestamp(todoToToggle.createdAt),
          completedAt: '',
        },
        ...current,
      ]);
    },
    [todos, trackTodosFeatureUse],
  );

  const editEntry = useCallback((todoId, newText) => {
    let didTrackEdit = false;
    trackTodosFeatureUse();
    setTodos((current) =>
      current.map((todo) => {
        if (todo.id === todoId) {
          const textChanged = todo.text !== newText;
          if (textChanged && !didTrackEdit) {
            didTrackEdit = true;
            track('todo_action', {
              app_surface: getAppSurface(),
              action_type: 'edit',
            });
          }
          return {
            ...todo,
            text: newText,
            createdAt: textChanged ? nowIsoTimestamp() : todo.createdAt,
          };
        }
        return todo;
      }),
    );
  }, [trackTodosFeatureUse]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveDragId(null);
      setActiveDragWidth(null);
      return;
    }

    setTodos((currentTodos) => {
      const oldIndex = currentTodos.findIndex((todo) => todo.id === active.id);
      const newIndex = currentTodos.findIndex((todo) => todo.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return currentTodos;
      return arrayMove(currentTodos, oldIndex, newIndex);
    });

    setActiveDragId(null);
    setActiveDragWidth(null);
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
    setActiveDragWidth(event.active?.rect?.current?.initial?.width || null);
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    setActiveDragWidth(null);
  };

  const greeting = useMemo(() => {
    if (name === 'Chloe Byun') {
      return easterEggMessage;
    }
    return message;
  }, [easterEggMessage, message, name]);

  const activeDragTodo = useMemo(() => {
    if (activeDragId === null) return null;
    return todos.find((todo) => todo.id === activeDragId) || null;
  }, [activeDragId, todos]);

  const canAddTodo = useMemo(() => sanitizeText(newTodoValue), [newTodoValue]);

  useEffect(() => {
    if (name !== 'Chloe Byun') {
      setNameHearts([]);
      clearNameHeartTimeouts();
      return undefined;
    }

    spawnNameHeart();
    const intervalId = window.setInterval(() => {
      if (Math.random() < 0.56) spawnNameHeart();
    }, 760);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [clearNameHeartTimeouts, name, spawnNameHeart]);

  useEffect(() => {
    return () => {
      clearNameHeartTimeouts();
    };
  }, [clearNameHeartTimeouts]);

  return (
    <div className={styles.todoRoot}>
      <div className={styles.flex}>
        <h2 className={cx(styles.interBold, styles.title, styles.inline)}>
          {greeting}
          {name && (
            <span
              className={cx(styles.cursor, name === 'Chloe Byun' && styles.nameWithHearts)}
              onClick={() => {
                clearNameHeartTimeouts();
                setNameHearts([]);
                setName('');
                window.setTimeout(() => {
                  if (nameInputRef.current) nameInputRef.current.focus();
                }, 120);
              }}
            >
              {name === 'Chloe Byun' && nameHearts.length > 0 && (
                <span className={styles.nameHeartLayer} aria-hidden="true">
                  {nameHearts.map((heart) => (
                    <span
                      key={heart.id}
                      className={styles.nameHeart}
                      style={{
                        left: `${heart.left}%`,
                        fontSize: `${heart.size}px`,
                        animationDuration: `${heart.duration}ms`,
                        '--name-heart-origin-y': `${heart.originY}%`,
                        '--name-heart-offset-x': `${heart.offsetX}px`,
                        '--name-heart-burst-x': `${heart.burstX}px`,
                        '--name-heart-burst-y': `${heart.burstY}px`,
                        '--name-heart-drift-mid': `${heart.driftMid}px`,
                        '--name-heart-rise-mid': `${heart.riseMid}px`,
                        '--name-heart-drift-end': `${heart.driftEnd}px`,
                        '--name-heart-rise-end': `${heart.riseEnd}px`,
                        '--name-heart-rotate-start': `${heart.rotateStart}deg`,
                        '--name-heart-rotate-end': `${heart.rotateEnd}deg`,
                        '--name-heart-scale-start': heart.scaleStart,
                        '--name-heart-scale-mid': heart.scaleMid,
                        '--name-heart-scale-end': heart.scaleEnd,
                        '--name-heart-opacity': heart.peakOpacity,
                      }}
                    >
                      ♥
                    </span>
                  ))}
                </span>
              )}
              <span className={styles.nameText}>{name === 'Chloe Byun' ? easterEggName : name}</span>
            </span>
          )}
        </h2>

        {!name && (
          <input
            id="displayNameInput"
            ref={nameInputRef}
            className={cx(styles.nameField, styles.inline)}
            onKeyDown={handleNameEnter}
            placeholder="your name"
            autoComplete="nickname"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
          />
        )}
      </div>

      <div className={styles.textFieldContainer}>
        <textarea
          ref={newTodoRef}
          id="newTodo"
          className={cx(styles.textField, styles.textFieldWithAction)}
          rows="1"
          placeholder="What needs to be done?"
          value={newTodoValue}
          onChange={(event) => setNewTodoValue(event.target.value)}
          onKeyDown={handleTodoEnter}
        />
        {canAddTodo ? (
          <button
            type="button"
            className={styles.addTodoButton}
            onClick={handleAddTodoClick}
            aria-label="Add todo"
          >
            <span className={styles.addTodoEnterIcon} aria-hidden="true"></span>
          </button>
        ) : (
          <button
            type="button"
            className={styles.shortcutHintButton}
            onClick={focusNewTodoInput}
            aria-label={`Focus todo input (${shortcutLabel})`}
            title={shortcutLabel}
          >
            {shortcutHintText}
          </button>
        )}
      </div>

      <div className={styles.containTodo}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={todos.map((todo) => todo.id)}
            strategy={verticalListSortingStrategy}
          >
            {todos.map((todo) => (
              <SortableTodoItem
                key={todo.id}
                todo={todo}
                onDelete={deleteTodo}
                onToggle={toggleCompleted}
                onEdit={editEntry}
              />
            ))}
          </SortableContext>

          <DragOverlay
            adjustScale={false}
            dropAnimation={{
              duration: 220,
              easing: 'cubic-bezier(0.2, 0, 0, 1)',
            }}
          >
            {activeDragTodo && (
              <div
                className={styles.floating}
                style={activeDragWidth ? { width: `${activeDragWidth}px` } : undefined}
              >
                <TodoItem
                  todo={activeDragTodo}
                  text={activeDragTodo.text}
                  id={activeDragTodo.id}
                  isCompleted={false}
                  deleteTodo={deleteTodo}
                  toggleCompleted={toggleCompleted}
                  editEntry={editEntry}
                  isDragging
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {completedTodos.length > 0 && (
        <div className={styles.containTodo}>
          <div className={styles.completedHeadingContainer}>
            <h2 className={styles.completedHeading}>Completed</h2>
            <button
              type="button"
              className={styles.completedHeadingContainerAction}
              onClick={() => setCompletedTodos([])}
            >
              Clear all
            </button>
          </div>

          {completedTodos.map((todo) => (
            <TodoItem
              key={todo.id}
              todo={todo}
              text={todo.text}
              id={todo.id}
              isCompleted={true}
              deleteTodo={deleteTodo}
              toggleCompleted={toggleCompleted}
              editEntry={editEntry}
            />
          ))}
        </div>
      )}
    </div>
  );
}

Todo.propTypes = {
  message: PropTypes.string,
  onHighestIdChange: PropTypes.func,
};
